import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Calculator, Save, AlertTriangle, Info, CheckCircle, Settings, X} from 'lucide-react';
import { PARAM_TYPES, CALC_MODES, RESTRICTION_TYPES, RESTRICTION_ACTIONS } from './constants';

export default function ModalsCustomRestriction({
  dialogOpen, setDialogOpen,
  editMode, setEditMode,
  formData, setFormData: setFormData,
  handleInputChange,
  handleSubmit,
  importDialogOpen, setImportDialogOpen,
  importData,
  confirmImport,
  fileInputRef: _fileInputRef,
  handleFileImport: _handleFileImport,
  previewDialogOpen, setPreviewDialogOpen,
  previewParams,
  customParamDialogOpen, setCustomParamDialogOpen,
  editingCustomParam,
  customParamForm, setCustomParamForm,
  handleSaveCustomParam,
  toggleApplicableA,
  restrictionDialogOpen, setRestrictionDialogOpen,
  editingRestriction,
  restrictionForm, setRestrictionForm,
  handleSaveRestriction,
  territoiresDisponibles,
  ajustementDialogOpen, setAjustementDialogOpen,
  editingAjustement,
  ajustementForm, setAjustementForm,
  handleCreateAjustement,
  handleUpdateAjustement,
  handleEdit}) {
  return (
    <>
        <Dialog open={customParamDialogOpen} onOpenChange={setCustomParamDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5 text-purple-600" />
                {editingCustomParam ? 'Modifier le paramètre' : 'Nouveau paramètre personnalisé'}
              </DialogTitle>
              <DialogDescription>
                Ajoutez une taxe, contribution, frais ou autre paramètre de calcul
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Nom */}
              <div className="space-y-2">
                <Label htmlFor="customName">Nom du paramètre *</Label>
                <Input
                  id="customName"
                  value={customParamForm.nom}
                  onChange={(e) => setCustomParamForm({ ...customParamForm, nom: e.target.value })}
                  placeholder="Ex: Taxe environnementale"
                />
              </div>

              {/* Type et Mode de calcul */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type</Label>
                  <select
                    value={customParamForm.type}
                    onChange={(e) => setCustomParamForm({ ...customParamForm, type: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    {PARAM_TYPES.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.icon} {type.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Mode de calcul</Label>
                  <select
                    value={customParamForm.mode_calcul}
                    onChange={(e) => setCustomParamForm({ ...customParamForm, mode_calcul: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    {CALC_MODES.map(mode => (
                      <option key={mode.value} value={mode.value}>{mode.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Valeur */}
              <div className="space-y-2">
                <Label htmlFor="customValue">
                  Valeur 
                  {customParamForm.mode_calcul === 'pourcentage' && ' (%)'}
                  {customParamForm.mode_calcul === 'fixe' && ' (€)'}
                  {customParamForm.mode_calcul === 'coefficient' && ' (multiplicateur)'}
                </Label>
                <Input
                  id="customValue"
                  type="number"
                  step={customParamForm.mode_calcul === 'coefficient' ? '0.0001' : '0.01'}
                  value={customParamForm.valeur}
                  onChange={(e) => setCustomParamForm({ ...customParamForm, valeur: parseFloat(e.target.value) || 0 })}
                  className="font-mono"
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="customDesc">Description (optionnel)</Label>
                <Input
                  id="customDesc"
                  value={customParamForm.description}
                  onChange={(e) => setCustomParamForm({ ...customParamForm, description: e.target.value })}
                  placeholder="Description du paramètre..."
                />
              </div>

              {/* Applicable à */}
              <div className="space-y-2">
                <Label>Applicable à</Label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { value: 'gros_oeuvre', label: 'Gros Œuvre' },
                    { value: 'second_oeuvre', label: 'Second Œuvre' },
                    { value: 'intellectuels', label: 'Prof. Intellectuels' },
                    { value: 'travaux_publics', label: 'Travaux Publics' }
                  ].map(cat => (
                    <button
                      key={cat.value}
                      type="button"
                      onClick={() => toggleApplicableA(cat.value)}
                      className={`px-3 py-1 rounded-full text-sm transition-all ${
                        customParamForm.applicable_a?.includes(cat.value)
                          ? 'bg-purple-600 text-white'
                          : 'bg-slate-700 text-white hover:bg-slate-200'
                      }`}
                    >
                      {customParamForm.applicable_a?.includes(cat.value) && (
                        <CheckCircle className="h-3 w-3 inline mr-1" />
                      )}
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Actif */}
              <div className="flex items-center space-x-3 p-3 bg-slate-50 rounded-lg">
                <input
                  type="checkbox"
                  id="customActive"
                  checked={customParamForm.is_active}
                  onChange={(e) => setCustomParamForm({ ...customParamForm, is_active: e.target.checked })}
                  className="h-5 w-5 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                />
                <Label htmlFor="customActive" className="font-normal cursor-pointer">
                  Paramètre actif (appliqué aux calculs)
                </Label>
              </div>
            </div>

            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={() => setCustomParamDialogOpen(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleSaveCustomParam}
                className="bg-purple-700 hover:bg-purple-700"
              >
                <Save className="h-4 w-4 mr-2" />
                {editingCustomParam ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog Restrictions Avancées */}
        <Dialog open={restrictionDialogOpen} onOpenChange={setRestrictionDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                {editingRestriction ? 'Modifier la restriction' : 'Nouvelle restriction avancée'}
              </DialogTitle>
              <DialogDescription>
                Définissez une règle métier complexe avec conditions multiples
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Informations de base */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                  <Info className="h-4 w-4" />
                  Informations générales
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="restrictionName">Nom de la restriction *</Label>
                    <Input
                      id="restrictionName"
                      value={restrictionForm.nom}
                      onChange={(e) => setRestrictionForm({ ...restrictionForm, nom: e.target.value })}
                      placeholder="Ex: Blocage CA < 10000€"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="restrictionPriority">Priorité</Label>
                    <Input
                      id="restrictionPriority"
                      type="number"
                      min="0"
                      value={restrictionForm.priorite}
                      onChange={(e) => setRestrictionForm({ ...restrictionForm, priorite: parseInt(e.target.value) || 0 })}
                      placeholder="0 = plus haute"
                    />
                  </div>
                </div>
              </div>

              {/* Type principal de restriction */}
              <div className="space-y-2">
                <Label>Type principal de restriction</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {RESTRICTION_TYPES.map(type => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setRestrictionForm({ 
                        ...restrictionForm, 
                        type: type.value,
                        valeur: '',
                        territoires_selectionnes: []
                      })}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        restrictionForm.type === type.value
                          ? 'border-red-500 bg-red-700 text-white'
                          : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{type.icon}</span>
                        <span className="font-medium text-sm">{type.label}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">{type.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Condition et Valeur - Format simplifié */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-4">
                <h4 className="font-semibold text-amber-800 flex items-center gap-2">
                  <Calculator className="h-4 w-4" />
                  Condition de déclenchement
                </h4>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs">Opérateur</Label>
                    <select
                      value={restrictionForm.condition}
                      onChange={(e) => setRestrictionForm({ ...restrictionForm, condition: e.target.value })}
                      className="w-full px-3 py-2 border border-amber-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                    >
                      <option value="equals">Égal à (=)</option>
                      <option value="not_equals">Différent de (≠)</option>
                      <option value="greater_than">Supérieur à (&gt;)</option>
                      <option value="less_than">Inférieur à (&lt;)</option>
                      <option value="greater_or_equal">Supérieur ou égal (≥)</option>
                      <option value="less_or_equal">Inférieur ou égal (≤)</option>
                      <option value="contains">Contient</option>
                      <option value="in_list">Dans la liste</option>
                      <option value="not_in_list">Pas dans la liste</option>
                    </select>
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label className="text-xs">
                      {restrictionForm.type === 'territoire' ? 'Sélectionner les territoires' : 'Valeur de comparaison'}
                    </Label>
                    
                    {/* Sélecteur de territoires si type = territoire */}
                    {restrictionForm.type === 'territoire' ? (
                      <div className="space-y-2">
                        <select
                          multiple
                          value={restrictionForm.territoires_selectionnes || []}
                          onChange={(e) => {
                            const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                            setRestrictionForm({ 
                              ...restrictionForm, 
                              territoires_selectionnes: selectedOptions,
                              valeur: selectedOptions.join(', ')
                            });
                          }}
                          className="w-full px-3 py-2 border border-amber-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm min-h-[120px]"
                        >
                          {territoiresDisponibles.filter(t => t.nom && t.nom.trim() !== '').map((territoire) => (
                            <option key={territoire.id} value={territoire.nom}>
                              {territoire.nom}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-amber-600">
                          Maintenez Ctrl (ou Cmd sur Mac) pour sélectionner plusieurs territoires
                        </p>
                        {restrictionForm.territoires_selectionnes && restrictionForm.territoires_selectionnes.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {restrictionForm.territoires_selectionnes.map((t, idx) => (
                              <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-amber-600 text-white text-xs rounded-full">
                                {t}
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newSelection = restrictionForm.territoires_selectionnes.filter(ter => ter !== t);
                                    setRestrictionForm({
                                      ...restrictionForm,
                                      territoires_selectionnes: newSelection,
                                      valeur: newSelection.join(', ')
                                    });
                                  }}
                                  className="hover:text-amber-900"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      /* Champ texte pour les autres types */
                      <Input
                        value={restrictionForm.valeur}
                        onChange={(e) => setRestrictionForm({ ...restrictionForm, valeur: e.target.value })}
                        placeholder={
                          restrictionForm.type === 'seuil_ca' ? 'Ex: 50000' :
                          restrictionForm.type === 'effectif' ? 'Ex: 10' :
                          'Valeur...'
                        }
                        className="border-amber-300 focus:ring-amber-500"
                      />
                    )}
                    {(restrictionForm.condition === 'in_list' || restrictionForm.condition === 'not_in_list') && restrictionForm.type !== 'territoire' && (
                      <p className="text-xs text-amber-600">Séparez les valeurs par des virgules</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Action à effectuer */}
              <div className="space-y-3">
                <Label className="font-semibold">Action à effectuer</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                  {RESTRICTION_ACTIONS.map(action => {
                    const activeClasses = {
                      red: 'border-red-600 bg-red-600 text-white',
                      amber: 'border-amber-600 bg-amber-600 text-white',
                      orange: 'border-orange-600 bg-orange-600 text-white',
                      green: 'border-green-600 bg-green-600 text-white',
                      blue: 'border-blue-600 bg-blue-600 text-white'
                    };
                    return (
                      <button
                        key={action.value}
                        type="button"
                        onClick={() => setRestrictionForm({ ...restrictionForm, action: action.value })}
                        className={`p-3 rounded-lg border-2 text-center transition-all ${
                          restrictionForm.action === action.value
                            ? activeClasses[action.color] || 'border-gray-600 bg-gray-600 text-white'
                            : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                        }`}
                      >
                        <span className="text-sm font-medium">{action.label}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Configuration spécifique à l'action */}
                {(restrictionForm.action === 'majorer' || restrictionForm.action === 'minorer') && (
                  <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg mt-2">
                    <Label className="text-sm text-orange-800">Pourcentage de {restrictionForm.action === 'majorer' ? 'majoration' : 'minoration'}</Label>
                    <div className="flex items-center gap-2 mt-2">
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={restrictionForm.action_config?.pourcentage || 0}
                        onChange={(e) => setRestrictionForm({ 
                          ...restrictionForm, 
                          action_config: { ...restrictionForm.action_config, pourcentage: parseFloat(e.target.value) || 0 }
                        })}
                        className="w-24 border-orange-300"
                      />
                      <span className="text-orange-700 font-medium">%</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Message personnalisé */}
              <div className="space-y-2">
                <Label htmlFor="restrictionMessage">Message affiché à l&apos;utilisateur</Label>
                <Input
                  id="restrictionMessage"
                  value={restrictionForm.message}
                  onChange={(e) => setRestrictionForm({ ...restrictionForm, message: e.target.value })}
                  placeholder="Ex: Cette opération n'est pas disponible pour votre profil..."
                />
              </div>

              {/* Options avancées */}
              <details className="p-4 bg-slate-50 rounded-lg">
                <summary className="font-semibold text-slate-700 cursor-pointer flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  Options avancées
                </summary>
                <div className="mt-4 space-y-4">
                  {/* Catégories applicables */}
                  <div className="space-y-2">
                    <Label className="text-sm">Catégories concernées</Label>
                    <div className="flex flex-wrap gap-2">
                      {['gros_oeuvre', 'second_oeuvre', 'intellectuels', 'travaux_publics'].map(cat => (
                        <label key={cat} className="flex items-center gap-2 px-3 py-2 bg-white border rounded-lg cursor-pointer hover:border-red-300">
                          <input
                            type="checkbox"
                            checked={(restrictionForm.applicable_categories || ['gros_oeuvre', 'second_oeuvre', 'intellectuels', 'travaux_publics']).includes(cat)}
                            onChange={(e) => {
                              const current = restrictionForm.applicable_categories || ['gros_oeuvre', 'second_oeuvre', 'intellectuels', 'travaux_publics'];
                              const newCats = e.target.checked 
                                ? [...current, cat]
                                : current.filter(c => c !== cat);
                              setRestrictionForm({ ...restrictionForm, applicable_categories: newCats });
                            }}
                            className="rounded border-slate-300 text-red-600"
                          />
                          <span className="text-sm">
                            {cat === 'gros_oeuvre' ? 'Gros Œuvre' :
                             cat === 'second_oeuvre' ? 'Second Œuvre' :
                             cat === 'intellectuels' ? 'PIB' : 'Travaux Publics'}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Modes applicables */}
                  <div className="space-y-2">
                    <Label className="text-sm">Modes RCDS concernés</Label>
                    <div className="flex gap-4">
                      {['annuel', 'nominative'].map(mode => (
                        <label key={mode} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={(restrictionForm.applicable_modes || ['annuel', 'nominative']).includes(mode)}
                            onChange={(e) => {
                              const current = restrictionForm.applicable_modes || ['annuel', 'nominative'];
                              const newModes = e.target.checked 
                                ? [...current, mode]
                                : current.filter(m => m !== mode);
                              setRestrictionForm({ ...restrictionForm, applicable_modes: newModes });
                            }}
                            className="rounded border-slate-300 text-red-600"
                          />
                          <span className="text-sm capitalize">{mode}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Note interne */}
                  <div className="space-y-2">
                    <Label className="text-sm">Note interne (visible admin seulement)</Label>
                    <Input
                      value={restrictionForm.message_interne || ''}
                      onChange={(e) => setRestrictionForm({ ...restrictionForm, message_interne: e.target.value })}
                      placeholder="Note pour les administrateurs..."
                    />
                  </div>
                </div>
              </details>

              {/* Actif */}
              <div className="flex items-center space-x-3 p-3 bg-red-50 rounded-lg border border-red-200">
                <input
                  type="checkbox"
                  id="restrictionActive"
                  checked={restrictionForm.is_active}
                  onChange={(e) => setRestrictionForm({ ...restrictionForm, is_active: e.target.checked })}
                  className="h-5 w-5 rounded border-slate-300 text-red-600 focus:ring-red-500"
                />
                <Label htmlFor="restrictionActive" className="font-normal cursor-pointer">
                  <span className="font-medium">Restriction active</span>
                  <span className="text-slate-500 text-sm ml-2">(appliquée immédiatement sur les calculs)</span>
                </Label>
              </div>
            </div>

            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={() => setRestrictionDialogOpen(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleSaveRestriction}
                className="bg-red-600 hover:bg-red-700"
                disabled={!restrictionForm.nom}
              >
                <Save className="h-4 w-4 mr-2" />
                {editingRestriction ? 'Mettre à jour' : 'Créer la restriction'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog Ajustement Montants */}
    </>
  );
}
